import { t } from '@lingui/macro'
import DOMPurify from 'dompurify'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { BookingFlowActions, BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'

import type { FlowState } from '../types/booking.type'

type SpecialisationInfoContentProps = {
  flowState: FlowState
  onAction: (action: 'continue-from-info', additionalData?: Record<string, unknown>) => void
  isLoading?: boolean
}

export const SpecialisationInfoContent = ({ flowState, onAction, isLoading }: SpecialisationInfoContentProps) => {
  const theme = useTheme()

  const handleContinue = () => {
    onAction('continue-from-info')
  }

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent>
        <FlexColumn gap={theme.UI.SpacingPx.Space1}>
          <H3 noMargin>
            {t({
              id: 'appointments.book.specialisationInfo.card.title',
              message: 'Over {specialisation}',
              values: {
                specialisation: flowState.specialisation?.title?.nl,
              },
            })}
          </H3>
          <StInfoContent
            // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(flowState.specialisation?.info?.nl || ''),
            }}
          />
        </FlexColumn>
      </BookingFlowCenteredContent>

      <BookingFlowActions>
        <Button variant='Primary' onClick={handleContinue} disabled={isLoading} isLoading={isLoading}>
          {t({
            id: 'global.continue',
            message: 'Ga verder',
          })}
        </Button>
      </BookingFlowActions>
    </BookingFlowLayout>
  )
}

const StInfoContent = styled.div`
  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
  line-height: ${({ theme }) => theme.Typography.BodyMediumRegular['line-height']};
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
  margin-bottom: 96px;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: ${({ theme }) => theme.UI.SpacingPx.Space3};
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space3};
    font-weight: 600;
  }

  p {
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space3};
    font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
    line-height: ${({ theme }) => theme.Typography.BodyMediumRegular['line-height']};
  }

  ul,
  ol {
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space2};
    padding-left: ${({ theme }) => theme.UI.SpacingPx.Space4};
  }

  li {
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space1};
  }

  a {
    color: ${({ theme }) => theme.Colors.Theme.Primary};
    text-decoration: underline;
  }
`
